import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { TaxIdFields } from "@/components/txn/TaxIdReminder";
import { TaxIdImport } from "@/components/txn/TaxIdImport";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

function PartyRow({ clientId, p, i, lang, onSaved }) {
  const [f, setF] = useState({ npwp: "", nik: "", id_tku: "" });
  const save = async () => {
    if (!f.npwp.trim() && !f.nik.trim()) { toast.error(fl("taxIdNeedOne", lang)); return; }
    try {
      await api.put("/tax-ids/party", { client_id: clientId, name: p.name, ...f });
      toast.success(fl("taxIdSaved", lang));
      onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid={`taxid-row-${i}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium" data-testid={`taxid-row-name-${i}`}>{p.name || <span className="italic text-muted-foreground">{fl("noParty", lang)}</span>}</span>
        <span className="text-[11px] text-muted-foreground num">{p.count} {fl("txCount", lang)}</span>
      </div>
      {p.name && (
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1"><TaxIdFields f={f} setF={setF} testid={`taxid-row-${i}`} lang={lang} /></div>
          <Button size="sm" className="h-9" onClick={save} data-testid={`taxid-row-save-${i}`}>{fl("saveToLedger", lang)}</Button>
        </div>
      )}
    </div>
  );
}

// Banner on the transaction list: taxable transactions (old and new) whose counterparty has no NPWP / NIK.
export function TaxIdBanner({ clientId, lang, reloadKey, onSaved }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    if (clientId) api.get(`/tax-ids/incomplete?client_id=${clientId}`).then(({ data }) => setD(data)).catch(() => {});
  }, [clientId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!d?.count) return null;
  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 px-3 py-2" data-testid="taxid-banner">
        <span className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><b className="num" data-testid="taxid-banner-count">{d.count}</b> {fl("taxIdBanner", lang)} · <span className="num">{d.parties.length}</span> {fl("taxIdParties", lang)}</span>
        </span>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="taxid-banner-open">{fl("complete", lang)}</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="taxid-dialog">
          <DialogHeader><DialogTitle>{fl("taxIdTitle", lang)}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{fl("taxIdDlgHint", lang)} {fl("tkuAuto", lang)}</p>
          <div className="flex flex-wrap gap-2" data-testid="taxid-dialog-import">
            <TaxIdImport clientId={clientId} lang={lang} onDone={() => { load(); if (onSaved) onSaved(); }} />
          </div>
          <div className="space-y-2">
            {d.parties.map((p, i) => <PartyRow key={p.name || `none-${i}`} clientId={clientId} p={p} i={i} lang={lang} onSaved={() => { load(); if (onSaved) onSaved(); }} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
